import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAboutViewStore } from '../../shared/store/aboutViewStore';
import { useCareer } from '../../shared/hooks/useApi/useCareer';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { MemberInfoValue } from '../../shared/component/headless/input/InputBody';
import { CareerData } from '../../shared/dto/EntityRepository';
import MoleculeValue from './molecules/MoleculeValue';
import CareerDateSection from './CareerDateSection';
import { useDeviceStore } from '../../shared/store/deviceStore';
import {
  AboutContentMode,
  AlertPosition,
  AlertType,
  CareerPeriodType,
  DeviceType,
} from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import {
  getCareerContentSummaryPlaceholder,
  getPageMessage,
} from '../../shared/constants/pageMessages';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import orangeIcon from '../../asset/icon/orange.png';
import seeIcon from '../../asset/icon/see.png';
import seeNotIcon from '../../asset/icon/see_not.png';
import { imHere } from '../../shared/component/Animation';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import Arrow, { ArrowWrapper } from '../../shared/component/Arrow';
import { openNewTab } from '../../shared/util/openNewTab';
import { parseHttpUrl } from '../../shared/util/urlValidation';
import { useCareerListStore } from '../../shared/store/careerStore';

interface CareerInfoProps {
  data: CareerData;
  displayAsPeriod: boolean;
  contentMode: AboutContentMode;
  showControls: boolean;
  reserveControls?: boolean;
  englishOnly?: boolean;
  hideYear?: boolean;
  showCurrentToggle?: boolean;
}

const CareerInfo: React.FC<CareerInfoProps> = ({
  data,
  displayAsPeriod,
  contentMode,
  showControls,
  reserveControls = false,
  englishOnly = false,
  hideYear = false,
  showCurrentToggle = true,
}) => {
  const { isExtended } = useAboutViewStore();
  const { isEditMode } = useEditMode();
  const { deleteCareer, updateCareerVisibility } = useCareer();
  const { deviceType } = useDeviceStore();
  const { enqueueAlert } = useStandardAlertStore();
  const updateCareerLocal = useCareerListStore(
    (state) => state.updateCareerLocal,
  );
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const isPeriodDisplay =
    data.periodType === CareerPeriodType.PERIOD &&
    (isEditMode || displayAsPeriod || data.isCurrent);
  const isEnglish = contentMode === AboutContentMode.ENG;
  const showEnInputChangeMark =
    isEditMode && isEnglish && !showControls && Boolean(data.contentEnChanged);
  const showButtonChangeMark =
    showControls &&
    Boolean(data.hasChanged) &&
    (Boolean(data.origChanged) || !data.contentEnChanged);
  const contentField = isEnglish ? 'contentEn' : 'content';
  const contentValue = data[contentField] ?? '';
  const hideSharedInfo = isEditMode && isEnglish && englishOnly;

  const handleMoveToRelatedUrl = () => {
    if (isEditMode || !data.relatedUrl) return;

    const url = parseHttpUrl(data.relatedUrl);
    if (!url) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_URL_FORMAT'),
      });
      return;
    }

    const isArchitraveUrl =
      url.hostname === 'architrave.work' ||
      url.hostname.endsWith('.architrave.work');

    if (isArchitraveUrl) {
      navigate(`${url.pathname}${url.search}${url.hash}`);
      return;
    }

    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_CAREER_RELATED_URL'),
      callBack: () => openNewTab(url.href),
    });
  };

  const handleTogglePublic = async () => {
    await updateCareerVisibility({
      careerId: data.id,
      isPublic: !data.isPublic,
    });
  };

  const handleToggleCurrent = (isCurrent: boolean) => {
    if (data.periodType !== CareerPeriodType.PERIOD) return;

    updateCareerLocal(
      data.id,
      isCurrent
        ? {
          isCurrent: true,
          endYear: '',
          endMonth: '',
          endDay: '',
        }
        : { isCurrent: false },
    );
  };

  const performDelete = async () => {
    await deleteCareer({ careerId: data.id });
  };

  const handleDelete = async () => {
    if (deviceType === DeviceType.MOBILE) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('CONFIRM_DELETE_CAREER'),
        callBack: performDelete,
      });
      return;
    }
    await performDelete();
  };

  return (
    <CareerInfoComp>
      <CareerContentComp $isPublic={data.isPublic}>
        {!hideSharedInfo && (
          <SharedCareerInfo>
            <CareerDateSection
              data={data}
              displayAsPeriod={isPeriodDisplay}
              isEditMode={isEditMode}
              isExtended={isExtended}
              contentMode={contentMode}
            onToggleCurrent={handleToggleCurrent}
            showControls={showControls}
            hideYear={hideYear}
            showCurrentToggle={showCurrentToggle}
          />
          </SharedCareerInfo>
        )}
        <CareerValueWrapper
          $isEmptyURL={data.relatedUrl === ''}
          $isPointDisplay={!isPeriodDisplay}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleMoveToRelatedUrl}
        >
          <MoleculeValue
            careerId={data.id}
            value={contentValue}
            targetField={contentField}
            inputStyle={MemberInfoValue}
            StyledDiv={Content}
            placeholder={getCareerContentSummaryPlaceholder(data.careerType)}
          />
          {showControls && isEditMode && isExtended && (
            <RelatedUrlEditor>
              <MoleculeValue
                careerId={data.id}
                value={data.relatedUrl}
                targetField="relatedUrl"
                inputStyle={MemberInfoValue}
                StyledDiv={Content}
                placeholder={getPageMessage('CAREER_RELATED_URL_PLACEHOLDER')}
              />
            </RelatedUrlEditor>
          )}
          {showEnInputChangeMark && (
            <EnChangedIconWrap>
              <ChangedIcon src={orangeIcon} alt="Unsaved changes" />
            </EnChangedIconWrap>
          )}
        </CareerValueWrapper>
      </CareerContentComp>
      {isEditMode && (showControls || reserveControls) && (
        <BtnContainer
          $hidden={!showControls}
          aria-hidden={!showControls}
        >
          <UniversalIconBtn
            size="fit"
            border={false}
            backgroundImg={data.isPublic ? seeIcon : seeNotIcon}
            backgroundImgHover={data.isPublic ? seeNotIcon : seeIcon}
            onClick={handleTogglePublic}
          />
          <UniversalIconBtn
            size="fit"
            border={false}
            backgroundImg={deleteCloseIcon}
            backgroundImgHover={deleteOpenIcon}
            onClick={handleDelete}
          />
          {showButtonChangeMark && (
            <ChangedIconWrap>
              <ChangedIcon src={orangeIcon} alt="Unsaved changes" />
            </ChangedIconWrap>
          )}
        </BtnContainer>
      )}
      {!isEditMode && isHovered && data.relatedUrl !== '' && (
        <ArrowWrapper>
          <Arrow />
        </ArrowWrapper>
      )}
    </CareerInfoComp>
  );
};

const CareerInfoComp = styled.div`
  position: relative;
  display: flex;
  height: fit-content;
  gap: clamp(8px, 1vw, 12px);
  margin-bottom: 8px;
`;

const CareerContentComp = styled.div<{ $isPublic: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: clamp(8px, 0.8vw, 16px);
  flex: 1;
  min-width: 0;
  opacity: ${({ $isPublic }) => $isPublic ? 1 : 0.5};
`;

const Content = styled.div`
  width: 100%;
  min-width: 0;
  white-space: pre-line;
  line-height: 1.3;
  ${({ theme }) => theme.typography.Body_04};
`;

const CareerValueWrapper = styled.div<{
  $isEmptyURL: boolean;
  $isPointDisplay: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: ${({ $isPointDisplay }) => $isPointDisplay ? 'flex-start' : 'stretch'};
  flex: 1;
  min-width: 0;
  gap: ${({ $isPointDisplay }) =>
    $isPointDisplay ? 'clamp(4px, 1vw, 8px)' : '4px'};
  cursor: ${({ $isEmptyURL }) => $isEmptyURL ? 'default' : 'pointer'};
`;

const SharedCareerInfo = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const BtnContainer = styled.div<{ $hidden: boolean }>`
  position: relative;
  align-self: flex-start;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 4px;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
`;

const ChangedIconWrap = styled.div`
  position: absolute;
  left: 100%;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  ${imHere}
`;

const EnChangedIconWrap = styled(ChangedIconWrap)`
  bottom: auto;
  height: 1.3em;
`;

const ChangedIcon = styled.img`
  width: 12px;
  height: 12px;
  margin: 4px;
`;

const RelatedUrlEditor = styled.div`
  display: flex;
  width: 100%;
`;

export default CareerInfo;
